import classNames from "classnames"
import type { Locale } from "date-fns"
import addMonths from "date-fns/addMonths"
import { useEffect, useState } from "react"
import type { FormElementStatus } from "../../Form/form"
import { Hint } from "../../Form/Hint"
import { CalendarDayPickerMonth } from "./CalendarDayPickerMonth"

type CalendarDayPickerPropsType = {
  numberOfMonths?: number
  firstMonthDate?: Date
  firstAvailableDate?: Date
  lastAvailableDate?: Date
  selectedDate?: Date | null
  hint?: {
    status?: FormElementStatus
    html: string
  }
  onChangeDate?: (date: Date, disabled: boolean) => boolean
  onLeaveDate?: (date: Date) => void
  onSelectDate?: (date: Date) => void
  locale?: Locale
}

export function CalendarDayPicker({
  firstMonthDate = new Date(),
  numberOfMonths = 1,
  hint,
  firstAvailableDate = new Date(),
  lastAvailableDate,
  selectedDate,
  onChangeDate,
  onLeaveDate,
  onSelectDate,
  locale,
}: CalendarDayPickerPropsType) {
  firstMonthDate.setHours(0, 0, 0, 0)
  firstAvailableDate.setHours(0, 0, 0, 0)

  const [internalSelectedDate, setInternalSelectedDate] = useState<
    Date | null | undefined
  >(selectedDate ?? null)

  useEffect(() => {
    setInternalSelectedDate(selectedDate ?? null)
  }, [selectedDate])

  const handleSelectDate = (date: Date) => {
    setInternalSelectedDate(date)
    onSelectDate?.(date)
  }

  const handleChangeDate = (date: Date, disabled: boolean) => {
    const processChange = onChangeDate
      ? onChangeDate(date, disabled) !== false
      : true

    if (!processChange || disabled) return
  }

  const months = [firstMonthDate]
  for (let i = 0; i < numberOfMonths - 1; i++) {
    months.push(addMonths(months[months.length - 1], 1))
  }

  return (
    <div className="cobalt-CalendarDayPicker">
      <div className="cobalt-CalendarDayPicker__months-container">
        {months.map((monthDate) => (
          <CalendarDayPickerMonth
            key={monthDate.getTime()}
            month={monthDate}
            selectedDate={internalSelectedDate}
            onSelectDate={handleSelectDate}
            onChangeDate={handleChangeDate}
            onLeaveDate={onLeaveDate}
            firstAvailableDate={firstAvailableDate}
            lastAvailableDate={lastAvailableDate}
            locale={locale}
          />
        ))}
      </div>

      <div
        className={classNames("cobalt-CalendarDayPicker__message", {
          "cobalt-CalendarDayPicker__message--show": !!hint,
        })}
      >
        {hint && <Hint status={hint.status}>{hint.html}</Hint>}
      </div>
    </div>
  )
}
